"use client";

import { cn } from "@repo/ui/lib/utils";
import Icon from "@repo/ui/components/ui/icon/icon";
import { useCustomer } from "@/data/consumer";
import { useOffersKpi } from "@/data/kpi";
import { endOfDay, startOfDay } from "date-fns";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from "@repo/ui/components/ui/popover";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

export default function KpiPopover() {
  const path = usePathname().split("/");
  const isProfile = path.includes("profile");
  const t = useTranslations("kpi-overlay");
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const { data: user } = useCustomer();
  const { data: kpi } = useOffersKpi({
    dateFrom: startOfDay(new Date(2025, 5)).toISOString(),
    dateTo: endOfDay(new Date()).toISOString(),
  });

  if (isProfile) {
    return null;
  }

  return (
    <div className="fixed bottom-[60px] right-[60px] max-md:bottom-[20px] max-md:right-[20px]">
      <Popover>
        <PopoverTrigger>
          <div
            className={cn(
              "animate-pulse-light shadow-toggle hover:opacity-90 rounded-full h-[70px] w-[70px] flex items-center justify-center bg-primary-magenta",
              "max-md:w-[45px] max-md:h-[45px]",
            )}
          >
            <Icon
              className="h-[45px] w-[45px] max-md:w-[25px] max-md:h-[25px]"
              icon="KPI"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="rounded-[20px] overflow-hidden text-[14px]"
        >
          <div className="">
            <div className="bg-primary-magenta text-primary-white p-4">
              <div className="flex justify-between items-center">
                <Icon
                  className="h-[25px] w-[25px] max-md:w-[25px] max-md:h-[25px]"
                  icon="KPI"
                />
                <PopoverClose asChild>
                  <Icon
                    className="light h-[25px] w-[25px] max-md:w-[25px] max-md:h-[25px] *:*:fill-primary-magenta cursor-pointer"
                    icon="Close"
                  />
                </PopoverClose>
              </div>
              <p className="mt-2 whitespace-pre-line leading-none">
                {t("title", { name: user?.firstName })}
              </p>
            </div>
            <div className="flex flex-col gap-2 p-4">
              <div className="flex gap-4 items-center">
                <Icon icon="Hands" className="w-[20px] h-[20px]" />
                <p>
                  {t("offers", { count: kpi?.offerKpiSetForPeriod?.accepted })}
                </p>
              </div>
              <div className="flex gap-4 items-center">
                <Icon icon="Discount" className="w-[20px] h-[20px]" />
                <p>
                  {t("savings", {
                    count: formatter
                      .format(kpi?.offerKpiSetForPeriod?.savedAmount)
                      .replace(",", "."),
                  })}
                </p>
              </div>
              <div className="flex gap-4 items-center border-b pb-2">
                <Icon
                  icon="Savings"
                  className="w-[20px] h-[20px] fill-primary-magenta/70"
                />
                <p>
                  {t("discount", {
                    discount: kpi?.offerKpiSetForPeriod?.savedPerc,
                  })}
                </p>
              </div>
              <PopoverClose asChild>
                <Link
                  href="/profile/wishlist"
                  className="flex gap-4 items-center"
                >
                  <Icon
                    icon="Heart"
                    className="w-[20px] h-[20px] fill-primary-magenta/70"
                  />
                  <p className="underline">{t("wishlist")}</p>
                </Link>
              </PopoverClose>
              <PopoverClose asChild>
                <Link
                  href="/profile/orders"
                  className="flex gap-4 items-center"
                >
                  <Icon
                    icon="Orders2"
                    className="w-[20px] h-[20px] fill-primary-magenta/70"
                  />
                  <p className="underline">{t("orders")}</p>
                </Link>
              </PopoverClose>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
